import { useCallback } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import useCrmObjectTypeTracking from 'framework-listing-lib/internal/hooks/useCrmObjectTypeTracking';
import { alertFailure, alertSuccess } from 'framework-listing-lib/utils/alerts';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { Metrics } from 'framework-listing-lib/internal/metrics';
import useApplicationId from 'framework-listing-lib/internal/hooks/useApplicationId';
import { useCurrentViewId, useViewsNamespace } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { convertCustomerDataViewToView, convertViewUpdateToCustomerDataView, convertViewToCustomerDataView } from '../utils/mapper';
import { useUpdateSingleViewByIdMutation } from 'customer-data-views-management/singleView/hooks/useUpdateSingleViewByIdMutation';
import { useUpdateSingleDefaultViewMutation } from 'customer-data-views-management/singleView/hooks/useUpdateSingleDefaultViewMutation';
import { useFetchSingleView } from 'customer-data-views-management/singleView/hooks/useFetchSingleView';

// This should be correct enough to prevent render-time errors
// while the currentView loads
// but should also not overwrite any actual existing view
// if somehow the mutation is called during the loading state
import { jsx as _jsx } from "react/jsx-runtime";
const PLACEHOLDER_VIEW = {
  id: '',
  name: '',
  columns: [],
  state: {},
  filterGroups: [],
  quickFilters: [],
  quickFilterProperties: [],
  namespaceName: '',
  objectTypeId: '',
  ownerId: undefined,
  private: false,
  teamId: undefined,
  type: 'STANDARD'
};
export function useSaveView() {
  const {
    trackCrmTableInteraction
  } = useCrmObjectTypeTracking();
  const objectTypeId = useObjectTypeId();
  const namespace = useViewsNamespace();
  const currentViewId = useCurrentViewId();
  const applicationId = useApplicationId();

  // We use the customer-data-views-management hook directly
  // instead of our context value
  // because we want to use the CDVM View interface
  const {
    data: currentViewData
  } = useFetchSingleView({
    id: currentViewId || '',
    objectTypeId,
    namespaceName: namespace
  });
  const [updateSingleViewMutation, {
    loading
  }] = useUpdateSingleViewByIdMutation({
    view: (currentViewData === null || currentViewData === void 0 ? void 0 : currentViewData.singleViewById) || PLACEHOLDER_VIEW
  });
  const saveView = useCallback(
  /**
   * Create new view if @view.id is not present.
   * Update view otherwise.
   */
  viewUpdate => {
    if (!viewUpdate.id) {
      return Promise.resolve();
    }
    return updateSingleViewMutation({
      variables: convertViewUpdateToCustomerDataView({
        viewUpdate,
        namespace
      })
    }).then(response => {
      var _response$data, _response$data2;
      if (!response.data) {
        return undefined;
      }
      trackCrmTableInteraction({
        action: 'update-view',
        viewId: String(response.data.updateSingleView.id)
      });
      Metrics.counter('update-view-success-v4', {
        application_id: applicationId
      }).increment();
      alertSuccess({
        message: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "frameworkListingLib.viewTabs.alerts.viewUpdate.success.message",
          options: {
            name: (_response$data = response.data) === null || _response$data === void 0 ? void 0 : _response$data.updateSingleView.name
          }
        })
      });
      return convertCustomerDataViewToView((_response$data2 = response.data) === null || _response$data2 === void 0 ? void 0 : _response$data2.updateSingleView);
    }).catch(() => {
      Metrics.counter('update-view-failure-v4', {
        application_id: applicationId
      }).increment();
      alertFailure({
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.viewTabs.alerts.viewUpdate.failure.message"
        }),
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.viewTabs.alerts.viewUpdate.failure.title"
        })
      });
    });
  }, [applicationId, namespace, trackCrmTableInteraction, updateSingleViewMutation]);
  return {
    loading,
    saveView
  };
}
export function useSaveDefaultView() {
  const objectType = useObjectTypeId();
  const namespace = useViewsNamespace();
  const currentViewId = useCurrentViewId();
  const applicationId = useApplicationId();

  // We use the customer-data-views-management hook directly
  // instead of our context value
  // because we want to use the CDVM View interface
  const {
    data: currentViewData
  } = useFetchSingleView({
    id: currentViewId || '',
    objectTypeId: objectType,
    namespaceName: namespace
  });
  const [updateDefaultViewMutation, {
    loading
  }] = useUpdateSingleDefaultViewMutation({
    view: (currentViewData === null || currentViewData === void 0 ? void 0 : currentViewData.singleViewById) || PLACEHOLDER_VIEW
  });
  const saveDefaultView = useCallback(view => {
    return updateDefaultViewMutation({
      variables: convertViewToCustomerDataView({
        view
      })
    }).then(() => {
      Metrics.counter('update-default-view-success', {
        application_id: applicationId
      }).increment();
    }).catch(() => {
      Metrics.counter('update-default-view-failure', {
        application_id: applicationId
      }).increment();
    });
  }, [applicationId, updateDefaultViewMutation]);
  return {
    loading,
    saveDefaultView
  };
}